import { defineStore, storeToRefs } from 'pinia';
import type { VideoMarket, VideoV1, Content } from '@ilteducation/content-registry-types';
import { computed, ref, watch } from 'vue';
import { useConfigHelper } from '@ilteducation/config';
import axios from 'axios';
import { createSearchId, SearchQuery, useSearchContexts } from '@ilteducation/content-search';
import { useUserContext } from '@ilteducation/ilt-auth';
import { getMarketLanguage } from './market-language';
import { ITranslated, Video } from './types';

const useVideos = defineStore('video', () => {
  const cdnDomain = process.env.VITE_CDN_DOMAIN;
  const videos = ref<Record<Video['id'], Video>>({});
  const searchResults = ref<Record<string, Video[]>>({});
  const isLoading = ref<Record<string, boolean>>({});
  const searchMode = ref(false);
  const { getApiUrl } = useConfigHelper();
  const { realm } = useUserContext();
  const searchContextsStore = useSearchContexts('Video');
  const { search: contextSearch, next } = searchContextsStore;
  const { result: contextResult, hasMore } = storeToRefs(searchContextsStore);

  const sortInArea = (areaId: string) => (a: Video, b: Video) =>
    (a.orderedAreas.find((area) => area.id === areaId)?.order ?? 0) -
    (b.orderedAreas.find((area) => area.id === areaId)?.order ?? 0);

  async function getVideoV1ById(videoId: string) {
    const resp = await axios.get(`${getApiUrl('content-registry', true)}/${videoId}`);
    return resp.data as VideoV1;
  }

  function convertVideo(video: VideoV1): Video {
    const marketLanguage = getMarketLanguage();

    const valid = (translated: ITranslated): translated is ITranslated & { value: never } =>
      'value' in translated &&
      translated.value !== undefined &&
      (translated.market === realm || translated.market === 'global');

    const res: Video = {
      ...video,
      resourceId: `${video.id}-${realm}`,
      categories: Array.from(new Set(video.categories)),
      title: video.title.find((title) => title.languageCode === marketLanguage)?.value || '',
      slug: video.slug[realm as keyof VideoV1['slug']] || '',
      description: video.description.find((d) => d.languageCode === marketLanguage)?.value || '',
      summary: video.summary.find((s) => s.languageCode === marketLanguage)?.value || '',
      thumbnail: video.previewImage[realm as keyof VideoV1['previewImage']]
        ? `${cdnDomain}/public/videos/${video.id}/${
            video.previewImage[realm as keyof VideoV1['previewImage']]?.fileName
          }?width=480&aspect_ratio=16:9`
        : undefined,
      previewImage: video.previewImage[realm as keyof VideoV1['previewImage']]
        ? `${cdnDomain}/public/videos/${video.id}/${
            video.previewImage[realm as keyof VideoV1['previewImage']]?.fileName
          }?width=1280&aspect_ratio=16:9`
        : undefined,
      introfilmsIds: video.introfilmsIds[realm as keyof VideoV1['introfilmsIds']] || {},
      introfilmsUids: video.introfilmsUids[realm as keyof VideoV1['introfilmsUids']] || {},
      narrations: video.narrations.filter(valid),
      subtitles: video.subtitles.filter(valid),
      bookLinks: {},
    };

    Object.entries(video.bookLinks).forEach(([categoryId, bookLink]) => {
      if (Array.isArray(bookLink)) {
        res.bookLinks[categoryId] = bookLink.filter(valid);
      }
    });

    return res;
  }

  function saveVideos(videosToStore: VideoV1[]): Video[] {
    return videosToStore.map((video) => {
      if (!videos.value[video.id]) {
        videos.value[video.id] = convertVideo(video);
      }
      return videos.value[video.id];
    });
  }

  function addVideos(contents: Content[]) {
    return saveVideos(contents.filter((c): c is VideoV1 => c.type === 'VideoV1'));
  }

  async function getVideoById(videoUid: string): Promise<Video> {
    if (videos.value[videoUid]) {
      return videos.value[videoUid];
    }
    if (isLoading.value[videoUid]) {
      return new Promise<Video>((resolve) => {
        const unwatch = watch(
          () => videos.value[videoUid],
          (vid) => {
            if (vid) {
              unwatch();
              resolve(vid);
            }
          },
          { immediate: true },
        );
      });
    }
    isLoading.value[videoUid] = true;
    const response = await getVideoV1ById(videoUid);
    saveVideos([response]);
    isLoading.value[videoUid] = false;
    return videos.value[videoUid];
  }

  function search(searchQuery: SearchQuery, lazy = false) {
    const contextId = createSearchId('Video', searchQuery);
    contextSearch(searchQuery, searchQuery.pagination?.size || 30);
    watch(
      () => contextResult.value(contextId) as VideoV1[],
      (newResult) => {
        if (!newResult.length) {
          return;
        }
        if (lazy && hasMore.value(contextId)) {
          next(contextId);
        } else {
          searchResults.value[contextId] = saveVideos(newResult);
        }
      },
      { immediate: true },
    );
    return contextId;
  }

  function getVideoBySlug(videoSlug: string) {
    const slugCheck = (video: VideoV1) => {
      if (video.slug[realm as VideoMarket] === videoSlug) {
        return true;
      }
      const slugHistory: string[] = video.slugHistory ? video.slugHistory[realm as VideoMarket] || [] : [];
      return slugHistory.map((s) => s.split('/').pop()).some((oldSlug) => oldSlug === videoSlug);
    };
    return new Promise<Video | undefined>((resolve) => {
      const searchQuery: SearchQuery = {
        q: videoSlug,
        searchFields: [`slug.${realm}:1:true`, `slugHistory.${realm}`],
        pagination: { size: 10 },
      };
      const searchId = createSearchId('Video', searchQuery);
      contextSearch(searchQuery, 10);
      let res: Video | undefined;
      const unwatch = watch(
        () => contextResult.value(searchId),
        async (videos) => {
          const videoMatch = videos.find((v) => slugCheck(v as VideoV1));
          if (videoMatch) {
            res = (await getVideoById(videoMatch.id)) as Video;
            unwatch();
            resolve(res);
          }
          if (!hasMore.value(searchId)) {
            unwatch();
            resolve(res);
          }
          next(searchId);
        },
        { immediate: true },
      );
    });
  }

  function getVideoByResourceIds(ids: string[]) {
    const videosInStore = ids.map((id) => videos.value[id]).filter((v) => v);
    const searchQuery: SearchQuery = {
      filter: {
        [`id`]: ids.filter((id) => !videosInStore.map((v) => v.id).includes(id)),
      },
    };
    const contextId = search(searchQuery, true);
    return [...videosInStore, ...(searchResults.value[contextId] ?? [])];
  }

  async function getVideosByAreaId(areaId: string) {
    return new Promise<Video[]>((resolve) => {
      const searchQuery: SearchQuery = {
        filter: {
          'orderedAreas.id': areaId,
        },
      };
      const searchId = createSearchId('Video', searchQuery);
      contextSearch(searchQuery, 30);
      const unwatch = watch(
        () => contextResult.value(searchId),
        async (areaVideos) => {
          if (areaVideos.length) {
            if (!hasMore.value(searchId)) {
              const vids = (await Promise.all(areaVideos.map((av) => getVideoById(av.id)))) as Video[];
              unwatch();
              resolve(vids.sort(sortInArea(areaId)));
            } else {
              next(searchId);
            }
          }
        },
        { immediate: true },
      );
    });
  }

  async function getDownloadUrl(id: string) {
    const res = await axios.get(`${getApiUrl('content-registry', true)}/${id}/contentUrls?allContent=true`);
    if (res.data && res.data.url) {
      return res.data.url.toString();
    }
    return '';
  }

  return {
    search,
    searchResults: computed(() => searchResults.value),
    getVideoById,
    getVideoByResourceIds,
    getVideoBySlug,
    getVideosByAreaId,
    videos: computed(() => videos.value),
    isLoading: computed(() => isLoading.value),
    getDownloadUrl,
    searchMode,
    sortInArea,
    addVideos,
  };
});
export default useVideos;
